import React, { useState, useMemo } from "react";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { DataViewMode, DataViewOption, ShadowMode } from "@/components/cards/widgets/TableFilter";
import MainTableWidget from "./MainTableWidget";
import { transformCardsToSectionFieldTableData } from "@/lib/utils/cardToTableTransformer";
import IconProvider from "@/components/ui/IconProvider";
import { SectionWithFields } from "./TableColumnSelector";
import { ColumnOrderDialog } from "./ColumnOrderDialog";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { getShadowProps } from "@/components/layout/element-layout/grid-layout/shadowUtils";
import { getDefaultColumnsForItemType, getFieldMetadataForItemType } from "@/lib/dataSources/shared/tableColumns";

export interface TemplateGroup {
  templateId: string;
  templateName: string;
  templateColor?: string;
  cards: CardData[];
}

interface TemplateGroupedTableDisplayProps {
  templateGroups: TemplateGroup[];
  itemType: string;
  dataViewMode: DataViewMode;
  selectedSections: string[];
  selectedFields: string[];
  availableSections: DataViewOption[];
  availableFields: DataViewOption[];
  rawItems: any[];
  onCardClick?: (card: CardData) => void;
  templateLookup?: Record<string, { id: string; name: string; color?: string }>;
  // Callback when a cell value changes in table view
  onCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
  // Shadow mode for determining left border color
  shadowMode?: ShadowMode;
  // Selected rows state for export functionality
  selectedRowsByGroup?: Record<string, Set<string>>;
  onSelectedRowsChange?: (selections: Record<string, Set<string>>) => void;
}

/**
 * Component that displays cards grouped by template in table format
 * Used when dataViewMode is "sections" or "fields"
 * Each template group has its own column selector dropdown
 */
export function TemplateGroupedTableDisplay({
  templateGroups,
  itemType,
  dataViewMode,
  selectedSections,
  selectedFields,
  availableSections,
  availableFields,
  rawItems,
  onCardClick,
  templateLookup = {},
  onCellValueChange,
  shadowMode = "folder",
  selectedRowsByGroup: externalSelectedRows,
  onSelectedRowsChange,
}: TemplateGroupedTableDisplayProps) {
  const { user } = useAuthStore();
  const currentUserId = user?.id;
  
  const [collapsedTemplates, setCollapsedTemplates] = useState<Set<string>>(new Set());
  
  // Per-template column selection state
  const [templateColumnSelections, setTemplateColumnSelections] = useState<Record<string, string[]>>({});

  // Per-template selected rows state - use external state if provided, otherwise use local state
  const [localSelectedRows, setLocalSelectedRows] = useState<Record<string, Set<string>>>({});
  
  // Use external state if provided (controlled mode), otherwise use local state (uncontrolled mode)
  const selectedRowsByGroup = externalSelectedRows ?? localSelectedRows;
  const setSelectedRowsByGroup = onSelectedRowsChange ?? setLocalSelectedRows;
  
  // Default columns - all metadata columns for this item type
  const defaultColumns = useMemo(() => getDefaultColumnsForItemType(itemType), [itemType]);
  
  // Field metadata for this item type
  const fieldMetadata = useMemo(() => getFieldMetadataForItemType(itemType), [itemType]);
  
  // Build template lookup with sections from raw items
  const enrichedTemplateLookup = useMemo(() => {
    const lookup: Record<string, { id: string; name: string; color?: string; sections?: SectionWithFields[] }> = {};
    
    // Start with existing template lookup
    Object.entries(templateLookup).forEach(([id, template]) => {
      lookup[id] = { ...template };
    });
    
    // Extract sections from raw items and add to lookup
    rawItems.forEach((item: any) => {
      if (item.blueprint_id && Array.isArray(item.data) && !lookup[item.blueprint_id]?.sections) {
        const sections: SectionWithFields[] = item.data.map((section: any) => ({
          id: section.id,
          title: section.title || 'Untitled Section',
          fields: (section.fields || []).map((field: any) => ({
            id: field.id,
            label: field.label || 'Untitled Field',
            type: field.type || 'text',
          })),
        }));
        
        if (lookup[item.blueprint_id]) {
          lookup[item.blueprint_id].sections = sections;
        } else {
          lookup[item.blueprint_id] = {
            id: item.blueprint_id,
            name: 'Unknown Template',
            sections,
          };
        }
      }
    });
    
    return lookup;
  }, [templateLookup, rawItems]);

  // Handle column selection change for a template
  const handleColumnsChange = (templateId: string, columns: string[]) => {
    setTemplateColumnSelections(prev => ({
      ...prev,
      [templateId]: columns,
    }));
  };

  // Handle selected rows change for a template group
  const handleSelectedRowsChange = (templateId: string, selectedRows: Set<string>) => {
    setSelectedRowsByGroup(prev => ({
      ...prev,
      [templateId]: selectedRows,
    }));
  };

  const toggleTemplateCollapse = (templateId: string) => {
    setCollapsedTemplates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  if (templateGroups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {templateGroups.map((group) => {
        const isCollapsed = collapsedTemplates.has(group.templateId);
        const selectedColumns = templateColumnSelections[group.templateId] || defaultColumns;
        const groupSelectedRows = selectedRowsByGroup[group.templateId] || new Set<string>();

        // Get raw items for this template group
        const groupRawItems = rawItems.filter(item => {
          const blueprintId = item.blueprint_id;
          if (group.templateId === 'no-template') {
            return !blueprintId;
          }
          return blueprintId === group.templateId;
        });

        // Transform cards to table data based on sections/fields view
        const tableData = transformCardsToSectionFieldTableData(
          group.cards,
          groupRawItems,
          dataViewMode,
          selectedSections,
          selectedFields,
          availableSections,
          availableFields,
          currentUserId
        ).map((item) => {
          if (item.type === 'row') {
            const card = group.cards.find(c => c.id === item.key);
            if (card) {
              // Determine border color based on shadow mode
              let borderColor: string | undefined;
              
              if (shadowMode === "folder") {
                // For folder mode, use original folder color logic
                borderColor = (card as any).subfolderColor || (card as any).folderColor;
              } else {
                // For other modes, use getShadowProps
                const shadowProps = getShadowProps(card, shadowMode);
                borderColor = shadowProps.shadowColor;
              }
              
              return {
                ...item,
                borderColor: borderColor,
              };
            }
          }
          return item;
        });

        // Skip groups with no table data
        if (tableData.length === 0) return null;

        return (
          <div key={group.templateId} className="template-group">
            {/* Template Header with Column Selector */}
            <div className="mb-4 flex items-center gap-2">
              <div 
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => toggleTemplateCollapse(group.templateId)}
              >
                <IconProvider
                  iconstring={isCollapsed ? "fa-chevron-right" : "fa-chevron-down"}
                  size={12}
                  className="text-sidebar-foreground ml-2 mr-2"
                />
                {group.templateColor && (
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: group.templateColor }}
                  />
                )}
                <div className="text-base font-medium text-sidebar-foreground">
                  {`${group.templateName} (${group.cards.length})`}
                </div>
              </div>
              
              {/* Column Manager - positioned next to the name, hidden when collapsed */}
              {!isCollapsed && (
                <ColumnOrderDialog
                  selectedColumns={selectedColumns}
                  onColumnsChange={(columns) => handleColumnsChange(group.templateId, columns)}
                  itemType={itemType}
                  sections={enrichedTemplateLookup[group.templateId]?.sections || []}
                />
              )}
            </div>

            {/* Table - only render if not collapsed */}
            {!isCollapsed && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <MainTableWidget
                  objectData={tableData}
                  selectedColumns={selectedColumns}
                  rawItems={groupRawItems}
                  fieldMetadata={fieldMetadata}
                  onCellValueChange={onCellValueChange}
                  onRowClick={onCardClick ? (rowKey) => {
                    const card = group.cards.find(c => c.id === rowKey);
                    if (card) onCardClick(card);
                  } : undefined}
                  selectedRows={groupSelectedRows}
                  onSelectedRowsChange={(selectedRows) => handleSelectedRowsChange(group.templateId, selectedRows)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

